import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeApiClient } from "../apiClient";
import {
  ADMIN_USER_STORAGE_KEY,
  noop,
  noopAsync,
  PUBLIC_BASE_URL,
} from "../authUtils";
import { AuthContext } from "./AuthContextCore";
import {
  persistStoredUser,
  readStoredUser,
  removeStoredUser,
} from "./authStorage";
import {
  loginAdminSession,
  postLogout,
  requestAdminAccessToken,
} from "./sessionApi";
import useTokenRefreshScheduler from "./useTokenRefreshScheduler";

export default function AdminAuthProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [hasRefreshCookie, setHasRefreshCookie] = useState(false);
  const restoreStartedRef = useRef(false);

  const clearLocalAdminState = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setHasRefreshCookie(false);
    setIsLoading(false);
    removeStoredUser(ADMIN_USER_STORAGE_KEY);
  }, []);

  const getAdminAccessToken = useCallback(
    async (force = false) => {
      if (!force && !hasRefreshCookie) return null;
      try {
        const accessToken = await requestAdminAccessToken();
        setToken(accessToken);
        return accessToken;
      } catch (err) {
        console.error("Error obtaining admin access token:", err.message);
        return null;
      }
    },
    [hasRefreshCookie],
  );

  const clearScheduledRefresh = useTokenRefreshScheduler({
    token,
    refreshToken: getAdminAccessToken,
    enabled: Boolean(token),
  });

  const loginAdmin = useCallback(
    async ({ email, secret }) => {
      try {
        const adminUser = await loginAdminSession({ email, secret });

        persistStoredUser(ADMIN_USER_STORAGE_KEY, adminUser);
        setHasRefreshCookie(true);
        setIsAuthenticated(true);
        setUser({ ...adminUser, provider: "admin" });

        const accessToken = await getAdminAccessToken(true);
        if (!accessToken) throw new Error("Failed to obtain admin access token");

        setToken(accessToken);
        setIsLoading(false);
        return { ok: true };
      } catch (err) {
        console.error("Admin login error:", err.message);
        removeStoredUser(ADMIN_USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        setHasRefreshCookie(false);
        setIsLoading(false);
        return { ok: false, error: err };
      }
    },
    [getAdminAccessToken],
  );

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    clearScheduledRefresh();
    clearLocalAdminState();

    let shouldResetLoggingOut = true;
    try {
      await postLogout("/admin/logout");
      if (typeof window !== "undefined") {
        shouldResetLoggingOut = false;
        window.location.replace(window.location.origin);
        return;
      }
    } finally {
      if (shouldResetLoggingOut) {
        setIsLoggingOut(false);
      }
    }
  }, [clearLocalAdminState, clearScheduledRefresh, isLoggingOut]);

  const getAccessTokenSilently = useCallback(
    async (force) => await getAdminAccessToken(Boolean(force)),
    [getAdminAccessToken],
  );

  const api = useMemo(
    () =>
      makeApiClient((force) =>
        isAuthenticated ? getAccessTokenSilently(force) : null,
      ),
    [isAuthenticated, getAccessTokenSilently],
  );
  const publicApi = useMemo(() => makeApiClient(null, PUBLIC_BASE_URL), []);

  useEffect(() => {
    if (restoreStartedRef.current) {
      return;
    }
    restoreStartedRef.current = true;

    const storedAdminUser = readStoredUser(ADMIN_USER_STORAGE_KEY);
    if (!storedAdminUser) {
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      setHasRefreshCookie(false);
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        const parsedAdminUser = JSON.parse(storedAdminUser);
        setHasRefreshCookie(true);
        const accessToken = await getAdminAccessToken(true);
        if (!accessToken) {
          removeStoredUser(ADMIN_USER_STORAGE_KEY);
          setHasRefreshCookie(false);
          setIsAuthenticated(false);
          setUser(null);
          setToken(null);
          setIsLoading(false);
          return;
        }
        setIsAuthenticated(true);
        setUser({ ...parsedAdminUser, provider: "admin" });
        setToken(accessToken);
        setIsLoading(false);
      } catch (err) {
        console.error("Error restoring admin session:", err);
        removeStoredUser(ADMIN_USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        setHasRefreshCookie(false);
        setIsLoading(false);
      }
    })();
  }, [getAdminAccessToken]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      isLoggingOut,
      isAuthenticated,
      user,
      token,
      login: noop,
      loginFeide: noop,
      loginAdmin,
      loginStudent: noopAsync,
      logout: handleLogout,
      refreshToken: getAdminAccessToken,
      setVippsAuth: noopAsync,
      setFeideAuth: noopAsync,
      getVippsAccessToken: noopAsync,
      getFeideAccessToken: noopAsync,
      getAccessTokenSilently,
      api,
      publicApi,
    }),
    [
      api,
      getAccessTokenSilently,
      getAdminAccessToken,
      handleLogout,
      isAuthenticated,
      isLoading,
      isLoggingOut,
      loginAdmin,
      publicApi,
      token,
      user,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

AdminAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
